/**
 * La entrega de un pedido de la página web (2026-11-02), leída de su nota.
 *
 * El servidor la escribe en la nota, un dato por renglón («Dirección de
 * entrega: …», «Ubicación: https://…»; ver `catalogo_web/pedidos.py`), para
 * que salga igual en el papel, en la venta y en cualquier pantalla vieja. Aquí
 * se separa para enseñarla aparte, con los botones del mapa y del repartidor.
 */

import { MODALIDADES } from "@/catalogo/pedido";
import type { Cotizacion } from "@/lib/types";

/** Los renglones de la entrega, tal como los escribe `pedidos.py`. */
const RENGLONES = {
  entrega: "Entrega: ",
  direccion: "Dirección de entrega: ",
  referencia: "Punto de referencia: ",
  ubicacion: "Ubicación: ",
} as const;

const TELEFONO = "Teléfono: ";

export interface EntregaDeLaNota {
  direccion: string | null;
  referencia: string | null;
  /** El enlace al mapa, sin lo de «aproximada». */
  mapa: string | null;
  /** «aproximada, ±1,2 km» si el teléfono no la dio precisa. */
  aproximada: string | null;
  telefono: string | null;
}

function valor(renglones: string[], prefijo: string): string | null {
  const r = renglones.find((x) => x.startsWith(prefijo));
  return r ? r.slice(prefijo.length).trim() || null : null;
}

export function entregaDeLaNota(nota: string | null): EntregaDeLaNota {
  const renglones = (nota ?? "").split("\n").map((r) => r.trim());
  const ubicacion = valor(renglones, RENGLONES.ubicacion);
  const enlace = ubicacion?.match(/^(https:\/\/\S+)\s*(?:\((.+)\))?$/);
  return {
    direccion: valor(renglones, RENGLONES.direccion),
    referencia: valor(renglones, RENGLONES.referencia),
    mapa: enlace?.[1] ?? null,
    aproximada: enlace?.[2] ?? null,
    telefono: valor(renglones, TELEFONO),
  };
}

/** La nota sin los renglones de la entrega, que se enseñan en su propio bloque. */
export function notaSinEntrega(nota: string | null): string {
  const prefijos = Object.values(RENGLONES);
  return (nota ?? "")
    .split("\n")
    .filter((r) => !prefijos.some((p) => r.trim().startsWith(p)))
    .join("\n")
    .trim();
}

/** «Delivery» o «Retiro en el local», si el pedido de la página la trae. */
export function nombreDeLaEntrega(c: Pick<Cotizacion, "origen" | "modalidad">): string | null {
  if (c.origen !== "catalogo_web") return null;
  return c.modalidad === "retiro" || c.modalidad === "delivery" ? MODALIDADES[c.modalidad] : null;
}

/**
 * El mensaje para el repartidor: quién, dónde y el mapa. Se abre en WhatsApp
 * sin número, y el propietario elige a quién mandarlo.
 *
 * Sin productos ni montos: el repartidor necesita llegar, y lo que se cobra
 * en la puerta lo decide el negocio (puede estar pagado por adelantado).
 */
export function mensajeParaElRepartidor(c: Pick<Cotizacion, "numero" | "contacto">, e: EntregaDeLaNota): string {
  const partes = [`*Delivery ${c.numero}*`];
  if (c.contacto) partes.push(`Cliente: ${c.contacto}`);
  if (e.telefono) partes.push(`Teléfono: ${e.telefono}`);
  if (e.direccion) partes.push(`Dirección: ${e.direccion}`);
  if (e.referencia) partes.push(`Punto de referencia: ${e.referencia}`);
  if (e.mapa) partes.push(`📍 Ubicación: ${e.mapa}${e.aproximada ? ` (${e.aproximada})` : ""}`);
  return partes.join("\n");
}

export function enlaceParaElRepartidor(mensaje: string): string {
  return `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
}
